import { Section } from 'components/Section';
import styles from './styles.scss';
import connect from 'components/Shared/connect';
import { useState, createElement, Dispatch, SetStateAction, memo, forwardRef, Ref } from 'react';
import {
  isFilterPage,
  getNbrOfHits,
  getTotalNbrOfHits,
  getShouldDisablePrice,
  isValueFacetFilter,
  getFilterPanelsWithoutFacet,
  getCategoryFilterFacet
} from 'components/Filter/selector';
import { NO_ELEMENT, rm } from 'utils';
import { types, getShowingAmountCaption } from '../types';
import cn from 'classnames';
import { FilterValue } from 'models/FilterValue';
import { FacetFilter } from 'models/FacetFilter';
import { Sort } from 'components/Filter/themes/BGH-RD3/desktop/types/sort';
import Overlay from 'components/Shared/Overlay';
import { DropdownButton } from 'components/Filter/themes/BGH-RD3/desktop/types/dropdownButton';
import { Choices } from 'components/Filter/themes/BGH-RD3/types/choices';
import { Checkbox } from './types/checkbox';
import { FilterDisplayType } from 'models/FilterDisplayType';
import { getQaDataAttr } from '@bygghemmait/utilities';

interface ContainerProps {
  children: React.ReactNode;
  className?: string;
  hide?: boolean;
}

const Container = ({ children, className, hide }: ContainerProps) => (
  <div
    style={{ minWidth: 'calc(100% - 1rem)' }}
    className={cn(styles.container, className, {
      [styles.hide]: hide
    })}
  >
    {children}
  </div>
);

interface SubFilterProps {
  subfilter: SetSubfilterArguments;
  shouldShow: boolean;
  categories: FacetFilter;
}

const shouldShow = (prev: SubFilterProps, curr: SubFilterProps) =>
  prev.shouldShow === curr.shouldShow && !curr.shouldShow;

const SubFilter = memo(({ subfilter, shouldShow, categories }: SubFilterProps) => {
  const hide = !shouldShow || !subfilter || !subfilter.active;
  return <Container hide={hide}>{createElement(types[categories.type], categories)}</Container>;
}, shouldShow);

interface SetSubfilterArguments {
  current: string;
  active: boolean;
}

interface FilterPanelProps extends FacetFilter {
  subfilter: SetSubfilterArguments;
  setSubfilter: Dispatch<SetStateAction<SetSubfilterArguments>>;
  setShouldDisplayAll: (shouldDisplayAll: boolean) => void;
}

const FilterPanel = ({
  name,
  setSubfilter,
  subfilter,
  displayName,
  setShouldDisplayAll,
  ...filterPanels
}: FilterPanelProps) => {
  const shouldDisableSlider = getShouldDisablePrice(filterPanels);
  if (shouldDisableSlider) {
    return NO_ELEMENT;
  }

  const isCurrent = name === subfilter.current;
  const shouldClose = subfilter.active && isCurrent;

  const onClick = () =>
    setSubfilter({
      current: name,
      active: !shouldClose
    });

  return (
    <div key={displayName} className={styles.filterPanelContainer}>
      <DropdownButton
        isFilterActive={filterPanels.active}
        displayName={displayName}
        onClick={onClick}
        shouldClose={shouldClose}
      />
      <SubFilter
        shouldShow={isCurrent}
        categories={{ ...filterPanels, name, displayName }}
        subfilter={subfilter}
      />
    </div>
  );
};

interface OwnFilterTypes extends FilterValue {
  filterPanels: FacetFilter[];
  nbrOfHits: number;
  totalNbrOfHits: number;
}

const getShouldDisplayShowMore = ({
  filterPanels,
  shouldDisplayAll
}: { shouldDisplayAll: boolean } & Pick<OwnFilterTypes, 'filterPanels'>): {
  panels: FacetFilter[];
  shouldDisplayShowMore: boolean;
} => {
  const MAX_PANEL_AMOUNT = 5;
  const DEFAULT = {
    panels: [],
    shouldDisplayShowMore: false
  };

  if (!Array.isArray(filterPanels)) {
    return DEFAULT;
  }

  const panelsToShow = filterPanels.slice(0, MAX_PANEL_AMOUNT);
  const panelsRemainder = filterPanels.length - panelsToShow.length;
  const shouldDisplayShowMore = panelsRemainder > 1 && !shouldDisplayAll;
  const panels = shouldDisplayShowMore ? panelsToShow : filterPanels;

  return {
    shouldDisplayShowMore,
    panels
  };
};

const _Filter = (
  { filterPanels, nbrOfHits, totalNbrOfHits }: OwnFilterTypes,
  ref: Ref<HTMLDivElement>
) => {
  const DEFAULT_FILTER = {
    current: '',
    active: false
  };
  const [subfilter, setSubfilter] = useState(DEFAULT_FILTER);
  const [shouldDisplayAll, setShouldDisplayAll] = useState(false);
  const onClick = () => setSubfilter(DEFAULT_FILTER);
  const { panels, shouldDisplayShowMore } = getShouldDisplayShowMore({
    filterPanels,
    shouldDisplayAll
  });
  const onClickDisplayAll = () => setShouldDisplayAll(true);
  const showingAmountCaption = getShowingAmountCaption(nbrOfHits);

  if (totalNbrOfHits <= 0) {
    return NO_ELEMENT;
  }

  return (
    <div ref={ref} className={styles.filterDesktop}>
      <Overlay transparent isFilterOverlay visible={subfilter.active} onClick={onClick} />
      <Section className={styles.sectionContainer}>
        <div className={styles.sectionContentWrapper}>
          <div className={styles.filterContainer}>
            <h3 className={styles.containerTitle}>{rm('filter_filter_button')}</h3>
            <div className={cn(styles.containerContent, styles.containerContentFilter)}>
              {panels.map(filterPanel =>
                filterPanel.type === FilterDisplayType.NoHeader &&
                isValueFacetFilter(filterPanel) ? (
                  <Checkbox key={filterPanel.name} className={styles.headless} {...filterPanel} />
                ) : (
                  <FilterPanel
                    key={filterPanel.name}
                    subfilter={subfilter}
                    setSubfilter={setSubfilter}
                    setShouldDisplayAll={setShouldDisplayAll}
                    {...filterPanel}
                  />
                )
              )}
              {shouldDisplayShowMore && (
                <DropdownButton
                  displayName={rm('filter_show_all_filters')}
                  onClick={onClickDisplayAll}
                  shouldClose={false}
                  isDropdown={false}
                />
              )}
            </div>
          </div>
          <div className={styles.sortContainer}>
            <h3 className={styles.containerTitle}>{rm('filter_sort_button')}</h3>
            <p className={styles.amountCaption} {...getQaDataAttr('amount-of-products')}>
              {showingAmountCaption}
            </p>
            <div className={cn(styles.containerContent, styles.containerContentSort)}>
              <Sort setSubfilter={setSubfilter} subfilter={subfilter} />
            </div>
          </div>
        </div>
        <Choices choiceClassName={styles.choice} />
      </Section>
    </div>
  );
};

interface StateProps {
  _filterPanels: OwnFilterTypes['filterPanels'];
}

export const FilterDesktop = connect<OwnFilterTypes, Record<string, unknown>, StateProps>(
  state => ({
    _filterPanels: !isFilterPage(state.currentPage) ? [] : state.currentPage.filterPanels,
    nbrOfHits: getNbrOfHits(state.currentPage),
    totalNbrOfHits: getTotalNbrOfHits(state.currentPage),
    categoryFilterFacet: getCategoryFilterFacet(state.currentPage)
  }),
  {},
  ({ _filterPanels, categoryFilterFacet, ...state }, dispatch, props) => ({
    ...state,
    ...dispatch,
    ...props,
    filterPanels: getFilterPanelsWithoutFacet(_filterPanels, categoryFilterFacet)
  }),
  { forwardRef: true }
)(forwardRef(_Filter));
